import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Button, Input, Select, Row, Col, DatePicker, Drawer, Descriptions,
  Space, Tooltip, Tabs, Modal, Form, InputNumber, Empty, Timeline, Alert, Badge,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, SafetyCertificateOutlined, DesktopOutlined,
  MobileOutlined, TabletOutlined, QuestionOutlined, CheckOutlined, CloseOutlined,
  StopOutlined, DisconnectOutlined, EyeOutlined, WarningOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import dayjs from 'dayjs';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import TableExportButton from '../../components/TableExportButton';
import {
  listerAppareils, detailAppareil, listerDemandes, compterDemandesEnAttente,
  approuverDemande, rejeterDemande, revoquerAppareil, bloquerAppareil,
  reinitialiserAppareils,
  STATUTS_APPAREIL, METHODES_CONFIANCE, TYPES_APPAREIL, MOTIFS_DEMANDE,
  EVENEMENTS_SECURITE,
} from '../../services/appareilAdminService';

const { RangePicker } = DatePicker;

/**
 * Console « Appareils de connexion ».
 *
 * <h3>Deux onglets, deux métiers</h3>
 * *Tous les appareils* est une vue de suivi : d'où se connecte-t-on, quels
 * postes sont de confiance, lesquels ont été coupés. *À valider* est une file
 * de travail : des agents attendent une décision, et chaque ligne doit pouvoir
 * être tranchée sans quitter l'écran.
 *
 * <h3>Pourquoi la modale d'approbation montre les autres appareils du compte</h3>
 * Une demande ne se juge pas dans le vide. Un agent qui a déjà trois postes
 * actifs et en déclare un quatrième depuis une IP inhabituelle ne présente pas
 * le même risque qu'un agent qui n'en a aucun et vient de changer de machine.
 * L'information est chargée à l'ouverture de la modale, à l'endroit où la
 * décision se prend - pas dans un écran voisin qu'il faudrait penser à ouvrir.
 *
 * <h3>Ce que l'écran ne fait pas</h3>
 * Il ne montre jamais de code de vérification, ni la valeur du cookie
 * d'appareil : ces deux secrets ne sortent pas du serveur, et une console
 * d'administration est précisément l'endroit où l'on serait tenté de les
 * afficher « pour aider au diagnostic ».
 */

const ICONES_TYPE = {
  ORDINATEUR: <DesktopOutlined />,
  MOBILE: <MobileOutlined />,
  TABLETTE: <TabletOutlined />,
  INCONNU: <QuestionOutlined />,
};

const tagStatut = (statut) => {
  const meta = STATUTS_APPAREIL[statut] || { label: statut, color: 'default' };
  return <Tag color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>;
};

const dateCourte = (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-');

export default function AppareilsAdmin() {
  const { t } = useTranslation('securite');
  const [onglet, setOnglet] = useState('appareils');

  /* ── Onglet « Tous les appareils » ── */
  const [appareils, setAppareils] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState(null);
  const [filtreType, setFiltreType] = useState(null);
  const [filtreIp, setFiltreIp] = useState('');
  const [periode, setPeriode] = useState(null);

  /* ── Onglet « À valider » ── */
  const [demandes, setDemandes] = useState([]);
  const [chargementDemandes, setChargementDemandes] = useState(false);
  const [nbEnAttente, setNbEnAttente] = useState(0);

  /* ── Panneau de détail ── */
  const [detail, setDetail] = useState(null);
  const [chargementDetail, setChargementDetail] = useState(false);

  /* ── Modale de décision ── */
  const [decision, setDecision] = useState(null); // { type, demande?, appareil?, contexte? }
  const [form] = Form.useForm();
  const [envoiDecision, setEnvoiDecision] = useState(false);

  const parametres = useCallback(() => {
    const p = {};
    if (recherche.trim()) p.recherche = recherche.trim();
    if (filtreStatut) p.statut = filtreStatut;
    if (filtreType) p.typeAppareil = filtreType;
    if (filtreIp.trim()) p.ip = filtreIp.trim();
    if (periode?.[0]) p.depuis = periode[0].format('YYYY-MM-DD');
    if (periode?.[1]) p.jusqua = periode[1].format('YYYY-MM-DD');
    return p;
  }, [recherche, filtreStatut, filtreType, filtreIp, periode]);

  // Page et taille sont toujours passees explicitement : les lire depuis l'etat
  // ici figerait la valeur dans la fermeture et rechargerait la mauvaise page
  // apres un changement de pagination.
  const chargerAppareils = useCallback(async (page = 1, size = 25) => {
    setChargement(true);
    try {
      const r = await listerAppareils(page - 1, size, parametres());
      setAppareils(r.items);
      setTotal(r.totalElements);
      setPagination({ current: page, pageSize: size });
    } catch (e) {
      toast.error(e?.response?.data?.message || i18n.t('securite:messages.chargementAppareilsImpossible'));
    } finally {
      setChargement(false);
    }
  }, [parametres]);

  const chargerDemandes = useCallback(async () => {
    setChargementDemandes(true);
    try {
      const r = await listerDemandes(0, 100, 'EN_ATTENTE');
      setDemandes(r.items);
      setNbEnAttente(r.totalElements);
      // La barre latérale affiche le même compteur : la tenir à jour évite
      // qu'un administrateur qui vient de vider la file voie encore le badge.
      window.dispatchEvent(new CustomEvent('cb:demandes-appareils', { detail: r.totalElements }));
    } catch (e) {
      toast.error(e?.response?.data?.message || i18n.t('securite:messages.chargementDemandesImpossible'));
    } finally {
      setChargementDemandes(false);
    }
  }, []);

  useEffect(() => {
    chargerAppareils(1, 25);
    chargerDemandes();
    compterDemandesEnAttente().then(setNbEnAttente).catch(() => { });
  }, [chargerAppareils, chargerDemandes]);

  const ouvrirDetail = async (lienId) => {
    setChargementDetail(true);
    setDetail({ chargement: true });
    try {
      setDetail(await detailAppareil(lienId));
    } catch (e) {
      setDetail(null);
      toast.error(e?.response?.data?.message || t('messages.detailIndisponible'));
    } finally {
      setChargementDetail(false);
    }
  };

  /* ── Décisions ── */
  const ouvrirDecision = async (type, cible) => {
    form.resetFields();
    setDecision({ type, ...cible, contexte: null });
    // Pour une approbation ou un rejet, on charge les autres appareils du compte :
    // c'est ce qui permet de décider en connaissance de cause.
    if ((type === 'approuver' || type === 'rejeter') && cible.appareilUtilisateurId) {
      try {
        const d = await detailAppareil(cible.appareilUtilisateurId);
        setDecision((prev) => (prev ? { ...prev, contexte: d } : prev));
      } catch { /* le contexte est un confort, pas un prérequis */ }
    }
  };

  const confirmerDecision = async () => {
    const valeurs = await form.validateFields();
    setEnvoiDecision(true);
    try {
      const { type } = decision;
      if (type === 'approuver') {
        await approuverDemande(decision.id, valeurs.commentaire, valeurs.dureeConfianceJours);
        toast.success(t('messages.appareilValide'));
      } else if (type === 'rejeter') {
        await rejeterDemande(decision.id, valeurs.commentaire);
        toast.success(t('messages.demandeRejetee'));
      } else if (type === 'revoquer') {
        await revoquerAppareil(decision.lienId, valeurs.commentaire);
        toast.success(t('messages.appareilRevoque'));
      } else if (type === 'bloquer') {
        await bloquerAppareil(decision.lienId, valeurs.commentaire);
        toast.success(t('messages.appareilBloque'));
      } else if (type === 'reinitialiser') {
        const r = await reinitialiserAppareils(decision.utilisateurTrackingId, valeurs.commentaire);
        toast.success(r?.message || t('messages.appareilsRevoques'));
      }
      setDecision(null);
      setDetail(null);
      chargerAppareils(pagination.current, pagination.pageSize);
      chargerDemandes();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.actionImpossible'));
    } finally {
      setEnvoiDecision(false);
    }
  };

  /* ── Colonnes ── */
  const colonnesAppareils = [
    {
      title: t('champs.utilisateur'), dataIndex: 'utilisateur', key: 'utilisateur', width: 220,
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email || '-'}</div>
        </div>
      ),
    },
    {
      title: t('champs.appareil'), dataIndex: 'libelle', key: 'libelle', width: 230,
      render: (v, r) => (
        <Space size={8}>
          <span style={{ color: '#6b7280' }}>{ICONES_TYPE[r.typeAppareil] || ICONES_TYPE.INCONNU}</span>
          <span>
            {v || '-'}
            {r.versionNavigateur && (
              <span style={{ color: '#9ca3af', fontSize: 12 }}> · v{r.versionNavigateur}</span>
            )}
          </span>
        </Space>
      ),
    },
    { title: t('champs.statut'), dataIndex: 'statut', key: 'statut', width: 150, render: tagStatut },
    {
      title: t('champs.confiance'), dataIndex: 'methodeConfiance', key: 'methodeConfiance', width: 190,
      render: (v, r) => (
        <div>
          <div>{METHODES_CONFIANCE[v] || '-'}</div>
          {r.confianceExpireLe && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {t('colonnes.confianceJusquAu', { date: dayjs(r.confianceExpireLe).format('DD/MM/YYYY') })}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('champs.derniereConnexion'), dataIndex: 'derniereConnexionLe', key: 'derniereConnexionLe', width: 175,
      render: (v, r) => (
        <div>
          <div>{dateCourte(v)}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{t('colonnes.nbConnexions', { nb: r.nbConnexions })}</div>
        </div>
      ),
    },
    {
      title: 'IP', dataIndex: 'derniereIp', key: 'derniereIp', width: 140,
      render: (v, r) => (
        <Tooltip title={t('colonnes.ipContexte')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v || '-'}</span>
          {r.dernierPays && <span style={{ color: '#6b7280' }}> · {r.dernierPays}</span>}
        </Tooltip>
      ),
    },
    {
      title: '', key: 'actions', width: 130, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={t('actions.detailJournal')}>
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => ouvrirDetail(r.id)} />
          </Tooltip>
          <Tooltip title={t('actions.retirerConfiance')}>
            <Button size="small" type="text" icon={<DisconnectOutlined />}
              disabled={r.statut === 'REVOQUE' || r.statut === 'BLOQUE'}
              onClick={() => ouvrirDecision('revoquer', { lienId: r.id, libelle: r.libelle, utilisateur: r.utilisateur })} />
          </Tooltip>
          <Tooltip title={t('actions.bloquer')}>
            <Button size="small" type="text" danger icon={<StopOutlined />}
              disabled={r.statut === 'BLOQUE'}
              onClick={() => ouvrirDecision('bloquer', { lienId: r.id, libelle: r.libelle, utilisateur: r.utilisateur })} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const colonnesDemandes = [
    {
      title: t('champs.utilisateur'), dataIndex: 'utilisateur', key: 'utilisateur', width: 260,
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: t('champs.appareil'), dataIndex: 'appareil', key: 'appareil', width: 240,
      render: (v, r) => (
        <Space size={8}>
          <span style={{ color: '#6b7280' }}>{ICONES_TYPE[r.typeAppareil] || ICONES_TYPE.INCONNU}</span>
          <span>{v || '-'}</span>
        </Space>
      ),
    },
    {
      title: t('colonnes.motifDeclare'), dataIndex: 'motif', key: 'motif', width: 170,
      render: (v) => <Tag color="orange" style={{ margin: 0 }}>{MOTIFS_DEMANDE[v] || v}</Tag>,
    },
    {
      title: t('champs.commentaire'), dataIndex: 'commentaire', key: 'commentaire',
      render: (v) => v || <span style={{ color: '#9ca3af' }}>-</span>,
    },
    { title: 'IP', dataIndex: 'ip', key: 'ip', width: 130 },
    { title: t('colonnes.demandeLe'), dataIndex: 'demandeLe', key: 'demandeLe', width: 160, render: dateCourte },
    {
      title: '', key: 'actions', width: 190, fixed: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" type="primary" icon={<CheckOutlined />}
            onClick={() => ouvrirDecision('approuver', r)}>{t('actions.approuver')}</Button>
          <Button size="small" danger icon={<CloseOutlined />}
            onClick={() => ouvrirDecision('rejeter', r)}>{t('actions.rejeter')}</Button>
        </Space>
      ),
    },
  ];

  const colonnesExport = [
    { title: t('champs.utilisateur'), dataIndex: 'utilisateur' },
    { title: t('champs.courriel'), dataIndex: 'email' },
    { title: t('champs.appareil'), dataIndex: 'libelle' },
    { title: t('champs.type'), dataIndex: 'typeAppareil' },
    { title: t('champs.statut'), dataIndex: 'statut' },
    { title: t('champs.confiance'), dataIndex: 'methodeConfiance' },
    { title: t('champs.derniereConnexion'), dataIndex: 'derniereConnexionLe' },
    { title: 'IP', dataIndex: 'derniereIp' },
    { title: t('colonnes.connexions'), dataIndex: 'nbConnexions' },
  ];

  /* ── Modale de décision ── */
  const titresDecision = {
    approuver: t('decision.titreApprouver'),
    rejeter: t('decision.titreRejeter'),
    revoquer: t('decision.titreRevoquer'),
    bloquer: t('decision.titreBloquer'),
    reinitialiser: t('decision.titreReinitialiser'),
  };

  const rendreModale = () => {
    if (!decision) return null;
    const { type, contexte } = decision;
    const rejet = type === 'rejeter';
    const destructif = type === 'bloquer' || type === 'reinitialiser';

    return (
      <Modal
        open
        title={titresDecision[type]}
        onCancel={() => setDecision(null)}
        onOk={confirmerDecision}
        confirmLoading={envoiDecision}
        okText={type === 'approuver' ? t('actions.approuver') : t('actions.confirmer')}
        okButtonProps={{ danger: rejet || destructif }}
        width={contexte ? 640 : 520}
        destroyOnClose
      >
        {type === 'approuver' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('decision.verifierIdentite')}
            description={t('decision.verifierIdentiteDetail')}
          />
        )}
        {type === 'reinitialiser' && (
          <Alert
            type="error" showIcon style={{ marginBottom: 16 }}
            message={t('decision.sessionsFermees')}
            description={t('decision.sessionsFermeesDetail')}
          />
        )}

        {contexte && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              {t('decision.autresAppareilsConnus')}
            </div>
            {contexte.autresAppareils?.length ? (
              <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                {contexte.autresAppareils.map((a) => (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                    borderBottom: '1px solid #f0f0f0', fontSize: 13,
                  }}>
                    <span style={{ color: '#6b7280' }}>{ICONES_TYPE[a.typeAppareil]}</span>
                    <span style={{ flex: 1 }}>{a.libelle}</span>
                    {tagStatut(a.statut)}
                    <span style={{ color: '#6b7280', fontSize: 12 }}>{dateCourte(a.derniereConnexionLe)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <Alert type="info" showIcon
                message={t('decision.aucunAutreAppareil')}
                description={t('decision.premierPoste')} />
            )}
          </div>
        )}

        <Form form={form} layout="vertical" initialValues={{ dureeConfianceJours: 30 }}>
          <Form.Item
            name="commentaire"
            label={rejet ? t('decision.motifRejet') : t('champs.commentaire')}
            rules={rejet || destructif
              ? [{ required: true, message: t('decision.motifObligatoire') }]
              : []}
            extra={rejet
              ? t('decision.motifRejetAide')
              : undefined}
          >
            <Input.TextArea rows={3} maxLength={500} showCount
              placeholder={rejet ? t('decision.exempleRejet') : t('decision.exempleApprobation')} />
          </Form.Item>

          {type === 'approuver' && (
            <Form.Item
              name="dureeConfianceJours"
              label={t('decision.dureeConfiance')}
              extra={t('decision.dureeConfianceAide')}
            >
              <InputNumber min={1} max={365} style={{ width: 160 }} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    );
  };

  /* ── Panneau de détail ── */
  const rendreDetail = () => {
    const a = detail?.appareil;
    return (
      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={620}
        title={a ? `${a.libelle} - ${a.utilisateur}` : t('detail.titre')}
        extra={a && (
          <Space>
            <Button size="small" icon={<DisconnectOutlined />}
              disabled={a.statut === 'REVOQUE' || a.statut === 'BLOQUE'}
              onClick={() => ouvrirDecision('revoquer', { lienId: a.id, libelle: a.libelle })}>
              {t('actions.revoquer')}
            </Button>
            <Button size="small" danger icon={<WarningOutlined />}
              onClick={() => ouvrirDecision('reinitialiser', {
                utilisateurTrackingId: detail?.appareil?.utilisateurTrackingId,
                libelle: a.utilisateur,
              })}
              disabled={!detail?.appareil?.utilisateurTrackingId}>
              {t('actions.compteCompromis')}
            </Button>
          </Space>
        )}
      >
        {chargementDetail || detail?.chargement ? null : !a ? <Empty /> : (
          <>
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 20 }}>
              <Descriptions.Item label={t('champs.utilisateur')}>{a.utilisateur} · {a.email || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('champs.statut')}>{tagStatut(a.statut)}</Descriptions.Item>
              <Descriptions.Item label={t('detail.methodeConfiance')}>{METHODES_CONFIANCE[a.methodeConfiance]}</Descriptions.Item>
              <Descriptions.Item label={t('detail.confianceJusquAu')}>
                {a.confianceExpireLe ? dayjs(a.confianceExpireLe).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.navigateur')}>
                {a.navigateur || '-'} {a.versionNavigateur ? `v${a.versionNavigateur}` : ''} · {a.systeme || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('champs.type')}>{TYPES_APPAREIL[a.typeAppareil] || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('detail.premiereVue')}>{dateCourte(a.creeLe)}</Descriptions.Item>
              <Descriptions.Item label={t('champs.derniereConnexion')}>
                {dateCourte(a.derniereConnexionLe)} · {t('colonnes.nbConnexions', { nb: a.nbConnexions })}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.derniereIp')}>{a.derniereIp || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('detail.echecsCode')}>{a.nbEchecsOtp}</Descriptions.Item>
              {a.revoqueLe && (
                <Descriptions.Item label={t('detail.revoque')}>
                  {t('detail.revoqueLePar', { date: dateCourte(a.revoqueLe), par: a.revoquePar || '-' })}<br />
                  <span style={{ color: '#6b7280' }}>{a.motifRevocation}</span>
                </Descriptions.Item>
              )}
            </Descriptions>

            {detail.autresAppareils?.length > 0 && (
              <>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('detail.autresAppareils')}</div>
                <div style={{ marginBottom: 20 }}>
                  {detail.autresAppareils.map((autre) => (
                    <div key={autre.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                      borderBottom: '1px solid #f0f0f0', fontSize: 13,
                    }}>
                      <span style={{ color: '#6b7280' }}>{ICONES_TYPE[autre.typeAppareil]}</span>
                      <span style={{ flex: 1 }}>{autre.libelle}</span>
                      {tagStatut(autre.statut)}
                      <Button size="small" type="link" onClick={() => ouvrirDetail(autre.id)}>{t('actions.voir')}</Button>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ fontWeight: 600, marginBottom: 12 }}>{t('detail.journal')}</div>
            {detail.evenements?.length ? (
              <Timeline
                items={detail.evenements.map((e) => {
                  const meta = EVENEMENTS_SECURITE[e.typeEvenement]
                    || { label: e.typeEvenement, color: 'default' };
                  return {
                    color: e.resultat === 'ECHEC' ? 'red' : (meta.color === 'default' ? 'gray' : meta.color),
                    children: (
                      <div>
                        <div style={{ fontWeight: 600 }}>{meta.label}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {dateCourte(e.horodatage)}
                          {e.ip ? ` · ${e.ip}` : ''}
                          {e.acteurLogin ? t('detail.parActeur', { acteur: e.acteurLogin }) : ''}
                        </div>
                        {e.details && (
                          <div style={{ fontSize: 12, color: '#9ca3af', wordBreak: 'break-word' }}>{e.details}</div>
                        )}
                      </div>
                    ),
                  };
                })}
              />
            ) : <Empty description={t('detail.aucunEvenement')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </>
        )}
      </Drawer>
    );
  };

  return (
    <Layout title={t('page.titreOnglet')}>
      <PageHeader
        titre={t('page.titre')}
        icone={<SafetyCertificateOutlined />}
        compte={total}
        unite={t('page.unite')}
      />

      <Tabs
        activeKey={onglet}
        onChange={setOnglet}
        items={[
          {
            key: 'appareils',
            label: t('page.ongletTous'),
            children: (
              <>
                <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
                  <Col xs={24} md={7}>
                    <Input
                      allowClear prefix={<SearchOutlined />}
                      placeholder={t('filtres.recherche')}
                      value={recherche}
                      onChange={(e) => setRecherche(e.target.value)}
                      onPressEnter={() => chargerAppareils(1, pagination.pageSize)}
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <Select allowClear style={{ width: '100%' }} placeholder={t('champs.statut')}
                      value={filtreStatut} onChange={setFiltreStatut}
                      options={Object.entries(STATUTS_APPAREIL).map(([v, m]) => ({ value: v, label: m.label }))} />
                  </Col>
                  <Col xs={12} md={4}>
                    <Select allowClear style={{ width: '100%' }} placeholder={t('filtres.typeAppareil')}
                      value={filtreType} onChange={setFiltreType}
                      options={Object.entries(TYPES_APPAREIL).map(([v, l]) => ({ value: v, label: l }))} />
                  </Col>
                  <Col xs={12} md={3}>
                    <Input allowClear placeholder="IP" value={filtreIp}
                      onChange={(e) => setFiltreIp(e.target.value)}
                      onPressEnter={() => chargerAppareils(1, pagination.pageSize)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Space.Compact style={{ width: '100%' }}>
                      <RangePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                        value={periode} onChange={setPeriode} />
                      <Button icon={<SearchOutlined />} type="primary"
                        onClick={() => chargerAppareils(1, pagination.pageSize)} />
                      <Button icon={<ReloadOutlined />} onClick={() => {
                        setRecherche(''); setFiltreStatut(null); setFiltreType(null);
                        setFiltreIp(''); setPeriode(null);
                        setTimeout(() => chargerAppareils(1, pagination.pageSize), 0);
                      }} />
                    </Space.Compact>
                  </Col>
                </Row>

                <Table
                  rowKey="id"
                  size="small"
                  loading={chargement}
                  dataSource={appareils}
                  columns={colonnesAppareils}
                  scroll={{ x: 1180 }}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    showTotal: (n) => t('page.total', { nb: n }),
                    onChange: (page, size) => chargerAppareils(page, size),
                  }}
                />
              </>
            ),
          },
          {
            key: 'demandes',
            label: (
              <Badge count={nbEnAttente} offset={[10, -2]} color="#f59e0b" size="small">
                <span>{t('page.ongletAValider')}</span>
              </Badge>
            ),
            children: (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                  <Button icon={<ReloadOutlined />} onClick={chargerDemandes}>{t('actions.actualiser')}</Button>
                </div>
                <Table
                  rowKey="id"
                  size="small"
                  loading={chargementDemandes}
                  dataSource={demandes}
                  columns={colonnesDemandes}
                  scroll={{ x: 1150 }}
                  pagination={false}
                  locale={{ emptyText: <Empty description={t('page.aucuneDemande')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                />
              </>
            ),
          },
        ]}
      />

      {/*  Export flottant  */}
      {/* RENDU HORS DES ONGLETS, ET CONDITIONNE A L'ONGLET ACTIF. Pose dans le volet
          « Appareils », il s'y serait trouve deux fois des que l'autre volet aurait ete
          visite : AntD garde montes les volets deja ouverts, et deux boutons flottants
          se superposent au meme coin. La condition sur `onglet` tient aussi lieu de
          reponse au volet « Demandes », qui n'a pas d'export : plutot que d'en inventer
          un, le bouton disparait avec le tableau qu'il exporte.

          `floating` suffit au placement : le composant pose lui-meme l'enveloppe et la
          reserve de place en bas de page. */}
      {onglet === 'appareils' && (
        <TableExportButton
          data={appareils}
          columns={colonnesExport}
          filename="appareils-de-connexion"
          title={t('page.titre')}
          floating
          count={appareils.length}
          showTotals={false}
          loadingLabel={t('page.preparationExport')}
        />
      )}

      {rendreDetail()}
      {rendreModale()}
    </Layout>
  );
}
